import { useStripe } from "@stripe/react-stripe-js";
import type { InjectedIntlProps } from "react-intl";
import {
  createPaymentSourceCard,
  createPaymentSourceCardPaymentIntent,
  createPaymentSourceCardSetup,
  createPaymentSourceInvoice,
  startSubscription,
  updateCustomerAddress,
  updateSubscription,
} from "src/billinglatest/clients/service.client";
import { useError } from "src/billinglatest/hooks/useError";
import { useLoader } from "src/billinglatest/hooks/useLoader";
import { useAppContext } from "src/hooks/useAppContext";
import type {
  PixiAddress,
  PixiPaymentMethod,
} from "../../hooks/usePixiWizardState";
import { pixiAddressToBillingAddress } from "./mappers/checkoutPayload";

/** The intl object supplied by `injectIntl` (react-intl v2 has no `useIntl`). */
type Intl = InjectedIntlProps["intl"];

export interface PurchaseInput {
  /** Plan tier group, e.g. "core" | "pro". */
  planGroup: string;
  /** Billing cycle, "year" | "month". */
  billingCycle: string;
  /**
   * The trialist's locked billing currency, resolved upstream (useTrialistCurrency)
   * and seeded into the wizard. Chargebee locks currency to the customer, so this
   * is authoritative — there is NO fallback: if it's missing, the purchase fails
   * rather than charging a currency the customer never agreed to.
   */
  currency: string;
  licenses: number;
  /** Selected payment method; only "credit_card" runs card setup + 3DS/SCA. */
  paymentMethod?: PixiPaymentMethod;
  /** Stripe paymentMethod id (pm_…) captured on the Payment step; card only. */
  cardPaymentMethodId?: string;
  /** Billing address to persist before subscribing — Chargebee requires one. */
  billingAddress: PixiAddress;
  /**
   * Set for the free-device deal: activates the subscription as a PIXI bundle
   * (adds the free device + paid shipping). The backend resolves the shipping
   * cost from its rate table using the address country + service, so the FE
   * sends only the destination and the service level — no amount, no item id.
   */
  bundle?: {
    shippingAddress: PixiAddress;
    shippingService: string;
  };
}

export interface UsePurchase {
  /** Submits the order; resolves true on success, false on handled failure. */
  purchase: (input: PurchaseInput) => Promise<boolean>;
  loading: boolean;
  /** Latest error message, surfaced from the charge steps (empty if none). */
  error: string;
}

/**
 * Orchestrates the Pixi Bundle purchase submit.
 *
 * This mirrors how the billing-latest page converts a NON-subscribed (trial)
 * user to paid — the trial org is already a Chargebee customer, so we do NOT
 * create one; we run the same SDK sequence as `Manage` → the form hooks:
 *
 * Step 1 — persist the billing address (updateCustomerAddress).
 * Step 2 — attach the payment method (card: setup + 3DS + attach, or invoice).
 * Step 3 — set the chosen plan + licences (updateSubscription, with 3DS/SCA),
 * using the trialist's locked currency passed in (no fallback).
 * Step 4 — activate the trial → paid (startSubscription). For the free-device
 * deal this passes the PIXI bundle option + shipping destination, so the backend
 * adds the free device and charges the shipping it resolves from its rate table.
 */
export const usePurchase = (intl: Intl): UsePurchase => {
  const stripe = useStripe();
  const loader = useLoader();
  const error = useError();
  const context = useAppContext();

  const t = (id: string, defaultMessage: string) =>
    intl.formatMessage({ id, defaultMessage });

  /**
   * Attach the Stripe-tokenised card (pm_…) to Chargebee, completing any 3DS on
   * the card setup. Mirrors `usePaymentMethodForm.saveCard` (the pm id was
   * already produced on the Payment step, so we skip re-tokenising here).
   */
  const attachCard = async (paymentMethodId: string, spaceId?: string) => {
    if (!stripe) {
      throw new Error(
        t(
          "pixi_wizard.checkout.error.payment_init",
          "Could not initialise the payment gateway. Please try again.",
        ),
      );
    }
    const intent = await createPaymentSourceCardSetup(
      { paymentMethodId },
      spaceId,
    );
    if (!intent?.id) {
      throw new Error(
        t(
          "pixi_wizard.checkout.error.card_validate",
          "There was a problem validating your card. Please try again.",
        ),
      );
    }
    if (intent.status !== "succeeded" && intent.secret) {
      const confirmed = await stripe.confirmCardSetup(intent.secret);
      if (confirmed.error?.message) {
        throw new Error(confirmed.error.message);
      }
    }
    await createPaymentSourceCard({ token: intent.id }, spaceId);
  };

  /**
   * Complete the 3DS/PSD2 challenge for the plan charge and return the Stripe
   * PaymentIntent id. Ported from `useSubscriptionForm.authenticateCard`.
   */
  const authenticateCard = async (
    planId: string,
    licenses: number,
    spaceId?: string,
  ): Promise<string | null> => {
    const intent = await createPaymentSourceCardPaymentIntent(
      { planId, licenses },
      spaceId,
    );
    if (!intent || intent.status === null) {
      return null;
    }
    if (intent.status === "canceled") {
      throw new Error(
        t(
          "pixi_wizard.checkout.error.card_cancelled",
          "Card verification was cancelled.",
        ),
      );
    }
    if (!stripe || !intent.secret) {
      throw new Error(
        t(
          "pixi_wizard.checkout.error.payment_init",
          "Could not initialise the payment gateway. Please try again.",
        ),
      );
    }
    if (
      intent.status !== "requires_action" &&
      intent.status !== "requires_confirmation"
    ) {
      return null;
    }
    const confirm = await stripe.confirmCardPayment(intent.secret);
    if (confirm.error?.message) {
      throw new Error(confirm.error.message);
    }
    if (!confirm.paymentIntent?.id) {
      throw new Error(
        t(
          "pixi_wizard.checkout.error.payment_auth",
          "There was a problem verifying your card. Please try again.",
        ),
      );
    }
    if (confirm.paymentIntent.status === "requires_action") {
      const action = await stripe.handleCardAction(intent.secret);
      if (action.error?.message) {
        throw new Error(action.error.message);
      }
      if (!action.paymentIntent?.id) {
        throw new Error(
          t(
            "pixi_wizard.checkout.error.payment_auth",
            "There was a problem verifying your card. Please try again.",
          ),
        );
      }
    }
    return confirm.paymentIntent.id;
  };

  const purchase = async ({
    planGroup,
    billingCycle,
    currency,
    licenses,
    paymentMethod,
    cardPaymentMethodId,
    billingAddress,
    bundle,
  }: PurchaseInput): Promise<boolean> => {
    loader.start();
    error.clear();
    const spaceId = context.currentSpace?.id;
    try {
      const isCard = paymentMethod === "credit_card";

      // 1. Persist the billing address (Chargebee needs one to charge).
      await updateCustomerAddress(
        pixiAddressToBillingAddress(billingAddress),
        spaceId,
      );

      // 2. Attach the payment method.
      if (isCard) {
        if (!cardPaymentMethodId) {
          throw new Error(
            t(
              "pixi_wizard.checkout.error.payment_incomplete",
              "Please complete your payment details.",
            ),
          );
        }
        await attachCard(cardPaymentMethodId, spaceId);
      } else if (paymentMethod === "invoice") {
        await createPaymentSourceInvoice(spaceId);
      } else {
        throw new Error(
          t(
            "pixi_wizard.checkout.error.payment_incomplete",
            "Please complete your payment details.",
          ),
        );
      }

      // 3. Build the plan id from the trialist's locked currency (resolved
      // upstream). No fallback: fail rather than charge a currency the customer
      // never agreed to. group/cycle are the user's choice; only currency is
      // constrained by Chargebee.
      if (!currency) {
        throw new Error(
          t(
            "pixi_wizard.checkout.error.currency_missing",
            "We couldn't confirm your billing currency. Please try again.",
          ),
        );
      }
      const planId = `${planGroup}-${billingCycle}-${currency.toLowerCase()}`;

      // 4. Set the chosen plan + licences (3DS/SCA for card payments).
      const paymentIntentId = isCard
        ? await authenticateCard(planId, licenses, spaceId)
        : null;
      await updateSubscription(
        { planId, quantity: licenses, paymentIntentId },
        spaceId,
      );

      // 5. Activate the trial → paid subscription. For the free-device deal,
      // pass the PIXI bundle option + shipping so the backend adds the device
      // and charges the shipping it resolves from its rate table.
      const startRequest: Parameters<typeof startSubscription>[0] = bundle
        ? {
            option: "pixi",
            shippingAddress: pixiAddressToBillingAddress(
              bundle.shippingAddress,
            ),
            shippingService: bundle.shippingService,
          }
        : undefined;
      await startSubscription(startRequest, spaceId);

      return true;
    } catch (e) {
      error.setMessage(
        e instanceof Error && e.message
          ? e.message
          : t(
              "pixi_wizard.checkout.purchase_error",
              "Something went wrong. Please try again.",
            ),
      );
      return false;
    } finally {
      loader.done();
    }
  };

  return { purchase, loading: loader.isLoading, error: error.message };
};
